import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornIPPFeedingQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { formatNewbornIPPFeedingBoolean } from "@/encounters/utils/format-newborn-ipp-feeding-boolean";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const FeedingDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPFeeding,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousFeedingAnswers = getQuestionnaireAnswers(previousVersion);
  const currentFeedingAnswers = getQuestionnaireAnswers(currentVersion);

  const previousFormula =
    previousFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Formula]?.valueBoolean;
  const currentFormula =
    currentFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Formula]?.valueBoolean;
  const previousNursing =
    previousFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Nursing]?.valueBoolean;
  const currentNursing =
    currentFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Nursing]?.valueBoolean;
  const previousNotes =
    previousFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Notes]?.valueString;
  const currentNotes = currentFeedingAnswers[NewbornIPPFeedingQuestionsLinkIds.Notes]?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Feeding</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Formula</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={formatNewbornIPPFeedingBoolean(currentFormula)}
                  previous={formatNewbornIPPFeedingBoolean(previousFormula)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Nursing</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={formatNewbornIPPFeedingBoolean(currentNursing)}
                  previous={formatNewbornIPPFeedingBoolean(previousNursing)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Formula</TextWrapper>
                <WordsDiff
                  current={formatNewbornIPPFeedingBoolean(currentFormula)}
                  previous={formatNewbornIPPFeedingBoolean(previousFormula)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Nursing</TextWrapper>
                <WordsDiff
                  current={formatNewbornIPPFeedingBoolean(currentNursing)}
                  previous={formatNewbornIPPFeedingBoolean(previousNursing)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default FeedingDiff;
